/**
 * Channel Status Mapping Save Policy (WS-3)
 *
 * Triggered when a status-mapping save is initiated (isSaving false -> true).
 * Dispatches `UpdateSystemIntegrationConfiguration` with the channel's existing
 * settings merged with the edited `orderStatusMapping` rows, so other settings
 * fields (url, cuit, ...) are preserved.
 */

import { Policy } from '@commandDrivenStateEngine/core/policies';
import { ClientLibraryType } from '@bett3r-dev/teselly-client-library';
import { ChannelsState } from '../state';
import { ChannelsCommand } from '../commands';

export const createChannelStatusMappingSavePolicy = (
  backend: ClientLibraryType
): Policy<ChannelsState, ChannelsCommand> => ({
  name: 'channel-status-mapping-save',

  shouldReact: ( prev, next ) => {
    // Trigger when isSaving changes from false to true
    return !prev.channelDetail.statusMapping.isSaving && next.channelDetail.statusMapping.isSaving;
  },

  onStateChange: async ({ next, ctx }) => {
    const { dispatch } = ctx;

    if ( next.channelDetail.mode.type !== 'view' ) return;

    const integrationId = next.channelDetail.mode.integrationId;
    const channel = next.salesChannels[integrationId];

    if ( !channel ) return;

    // Drop empty/partial rows before persisting.
    const orderStatusMapping = next.channelDetail.statusMapping.rows.filter(
      ( row ) => row.channelStatus.trim() !== '' && row.canonicalStatusId.trim() !== ''
    );

    // eslint-disable-next-line teselly/no-swallowed-command-dispatch -- console.error operator log + loading reset only, no user surface (TV1-2010 waiver, not a rethrow).
    try {
      await backend.commands.UpdateSystemIntegrationConfiguration({
        id: channel.id,
        body: {
          id: channel.id,
          accountId: channel.accountId,
          connectorId: channel.connectorId,
          integrationUserId: channel.integrationUserId,
          // Merge into existing settings to preserve other fields.
          settings: {
            ...( channel.settings ?? {}),
            orderStatusMapping
          }
        }
      });
    } catch ( error ) {
      console.error( 'Error saving channel status mapping:', error );
    } finally {
      dispatch(
        { type: 'integrations/channels/detail/loading/setSavingStatusMapping', payload: { isLoading: false }},
        { source: 'policy' }
      );
    }
  }
});
